import { t, Trans } from "@lingui/macro";
import { CheckIcon, XIcon } from "@phosphor-icons/react";
import { type ReactNode, useState } from "react";

type Cell = Readonly<{
  verdict?: "yes" | "no";
  detail?: ReactNode;
}>;

type CompareRow = Readonly<{
  label: ReactNode;
  cells: readonly [Cell, Cell, Cell, Cell, Cell];
}>;

const COLUMNS = [
  { id: "apply-edge", label: <Trans>Apply Edge</Trans> },
  { id: "teal", label: <Trans>Teal</Trans> },
  { id: "simplify", label: <Trans>Simplify</Trans> },
  { id: "lazyapply", label: <Trans>LazyApply</Trans> },
  { id: "jobscan", label: <Trans>Jobscan</Trans> },
] as const;

const ROWS: readonly CompareRow[] = [
  {
    label: <Trans>Price</Trans>,
    cells: [
      { detail: <Trans>$7.99/week, 14-day free trial</Trans> },
      { detail: <Trans>$13/week</Trans> },
      { detail: <Trans>Free plan; paid price shown in app</Trans> },
      { detail: <Trans>From $99/year</Trans> },
      { detail: <Trans>$49.95/month</Trans> },
    ],
  },
  {
    label: <Trans>Scores every new job against your CV</Trans>,
    cells: [
      { verdict: "yes", detail: <Trans>Daily</Trans> },
      { detail: <Trans>One saved job at a time</Trans> },
      { verdict: "yes" },
      { verdict: "no" },
      { detail: <Trans>Premium plan</Trans> },
    ],
  },
  {
    label: <Trans>Tailors your CV for each job</Trans>,
    cells: [
      { verdict: "yes", detail: <Trans>You edit before you use it</Trans> },
      { verdict: "yes" },
      { detail: <Trans>Paid plan</Trans> },
      { detail: <Trans>Not stated</Trans> },
      { verdict: "yes" },
    ],
  },
  {
    label: <Trans>You control every application</Trans>,
    cells: [
      { verdict: "yes", detail: <Trans>Review each one, or turn on auto-approve (beta)</Trans> },
      { verdict: "yes" },
      { detail: <Trans>Autopilot can submit without review</Trans> },
      { verdict: "no", detail: <Trans>Bulk-applies up to 1,500/day</Trans> },
      { verdict: "yes" },
    ],
  },
  {
    label: <Trans>GulfTalent and Wuzzuf jobs included</Trans>,
    cells: [
      { verdict: "yes" },
      { detail: <Trans>Not listed</Trans> },
      { detail: <Trans>Not listed</Trans> },
      { detail: <Trans>Not listed</Trans> },
      { detail: <Trans>Not listed</Trans> },
    ],
  },
  {
    label: <Trans>Arabic interface</Trans>,
    cells: [
      { verdict: "yes" },
      { detail: <Trans>Not listed</Trans> },
      { detail: <Trans>Not listed</Trans> },
      { detail: <Trans>Not listed</Trans> },
      { detail: <Trans>Not listed</Trans> },
    ],
  },
] as const;

const CompareCell = ({ cell }: { cell: Cell }) => (
  <div className="flex items-start gap-2">
    {cell.verdict === "yes" && (
      <CheckIcon
        role="img"
        aria-label={t`Yes`}
        size={18}
        weight="bold"
        className="mt-0.5 shrink-0 text-indigo-700 dark:text-indigo-300"
      />
    )}
    {cell.verdict === "no" && (
      <XIcon
        role="img"
        aria-label={t`No`}
        size={18}
        weight="bold"
        className="mt-0.5 shrink-0 text-muted-foreground"
      />
    )}
    {cell.detail && <span>{cell.detail}</span>}
  </div>
);

export const CompareSection = () => {
  const [selectedCompetitor, setSelectedCompetitor] = useState(1);

  return (
    <section className="scroll-mt-16 border-t border-border bg-muted/30">
      <div className="mx-auto max-w-[1280px] px-6 py-24">
        <div className="text-xs font-medium text-muted-foreground">
          <Trans>Compare your options</Trans>
        </div>
        <h2 className="mt-3 scroll-mt-20 text-balance text-[32px] font-semibold leading-[1.05] tracking-[-0.02em] sm:text-[40px]">
          <Trans>How Apply Edge compares</Trans>
        </h2>
        <p className="mt-4 max-w-[70ch] text-[13.5px] leading-relaxed text-muted-foreground">
          <Trans>
            Built for job seekers in the Gulf and MENA. Prices and features from each company's own
            website, checked 18 Oct 2026.
          </Trans>
        </p>

        <div data-testid="phone-comparison" className="mt-10 min-w-0 md:hidden">
          <div id="compare-with-label" className="mb-3 text-sm font-medium text-foreground">
            <Trans>Compare with</Trans>
          </div>
          <div
            role="tablist"
            aria-labelledby="compare-with-label"
            className="grid w-full min-w-0 grid-cols-2 gap-1 rounded-xl border border-border bg-surface p-1 min-[440px]:grid-cols-4"
          >
            {COLUMNS.slice(1).map((column, index) => (
              <button
                key={column.id}
                type="button"
                role="tab"
                aria-selected={selectedCompetitor === index + 1}
                className={`min-h-11 min-w-0 break-words rounded-lg px-2 py-2 text-center text-sm font-medium leading-tight transition-colors ${
                  selectedCompetitor === index + 1
                    ? "bg-indigo-50 text-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-100"
                    : "text-foreground hover:bg-muted"
                }`}
                onClick={() => {
                  setSelectedCompetitor(index + 1);
                }}
              >
                {column.label}
              </button>
            ))}
          </div>
          <div className="mt-5 min-w-0 divide-y divide-border overflow-hidden rounded-xl border border-border bg-surface shadow-sm">
            {ROWS.map((row, rowIndex) => (
              <div key={rowIndex} className="min-w-0 p-3">
                <h3 className="mb-2 text-sm font-medium text-foreground">{row.label}</h3>
                <div className="grid min-w-0 grid-cols-2 gap-2 text-[13px] leading-relaxed">
                  <div className="min-h-28 min-w-0 break-words rounded-lg bg-indigo-50 p-3 text-indigo-950 dark:bg-indigo-950/40 dark:text-indigo-100">
                    <div className="mb-2 font-semibold">{COLUMNS[0].label}</div>
                    <CompareCell cell={row.cells[0]} />
                  </div>
                  <div className="min-h-28 min-w-0 break-words rounded-lg bg-muted/40 p-3 text-foreground">
                    <div className="mb-2 font-semibold">{COLUMNS[selectedCompetitor].label}</div>
                    <CompareCell cell={row.cells[selectedCompetitor]} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 hidden max-w-full overflow-x-auto rounded-xl border border-border bg-surface shadow-sm md:block">
          <table className="w-full min-w-[872px] table-fixed border-collapse text-left text-[13px] leading-relaxed sm:min-w-[1080px]">
            <thead>
              <tr className="border-b border-border">
                <th
                  scope="col"
                  className="sticky left-0 z-20 w-[132px] bg-surface px-3 py-4 sm:w-[220px] sm:px-5"
                >
                  <span className="sr-only">{t`Feature`}</span>
                </th>
                {COLUMNS.map((column, index) => (
                  <th
                    key={column.id}
                    scope="col"
                    className={`w-[148px] px-3 py-4 text-[14px] font-semibold sm:w-[172px] sm:px-4 ${
                      index === 0
                        ? "bg-indigo-50 text-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-100"
                        : "text-foreground"
                    }`}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-b border-border last:border-b-0">
                  <th
                    scope="row"
                    className="sticky left-0 z-10 bg-surface px-3 py-4 font-medium text-foreground sm:px-5"
                  >
                    {row.label}
                  </th>
                  {row.cells.map((cell, cellIndex) => (
                    <td
                      key={COLUMNS[cellIndex].id}
                      className={`px-4 py-4 align-top ${
                        cellIndex === 0
                          ? "bg-indigo-50 text-indigo-950 dark:bg-indigo-950/40 dark:text-indigo-100"
                          : "text-foreground"
                      }`}
                    >
                      <CompareCell cell={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4">
          <a
            href="/auth/register"
            className="inline-flex items-center rounded-full bg-primary px-5 py-3 text-[14px] font-medium text-primary-foreground shadow-sm transition hover:bg-primary/90"
          >
            <Trans>Start your free trial</Trans>
          </a>
          <p className="text-xs leading-relaxed text-muted-foreground">
            <Trans>
              Competitor names are trademarks of their owners. Tell us if anything here is out of
              date.
            </Trans>
          </p>
        </div>
      </div>
    </section>
  );
};
